import { ChevronLeft, ChevronRight, FolderOpen, List, MoreHorizontal, Pencil, Plus, Search, Sparkles, Trash2, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useCreateWatchlist,
  useDeleteWatchlist,
  useTickersByWatchlist,
  useUpdateWatchlist,
  useWatchlists,
} from "@/hooks/useWatchlistQueries";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";

interface WatchlistSidebarProps {
  className?: string;
  isCollapsed?: boolean;
  onToggle?: () => void;
  inDrawer?: boolean; // Whether rendered in a drawer (mobile sheet)
}

export function WatchlistSidebar({ className = "", isCollapsed = false, onToggle, inDrawer = false }: WatchlistSidebarProps) {
  const t = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get selected watchlist from URL param (default to "all")
  const selectedWatchlistId = searchParams.get("watchlist") || "all";

  // Queries
  const { data: watchlists = [] } = useWatchlists();
  // Fetch "All Tickers" count (use large pageSize to get total count without fetching all tickers)
  const { data: allTickersData } = useTickersByWatchlist("all", { pageSize: 1 });
  const createWatchlist = useCreateWatchlist();
  const updateWatchlist = useUpdateWatchlist();
  const deleteWatchlist = useDeleteWatchlist();

  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedWatchlist, setSelectedWatchlist] = useState<(typeof watchlists)[number] | null>(null);
  const [newName, setNewName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Handle watchlist selection
  const handleSelectWatchlist = (id: string) => {
    setSearchParams({ watchlist: id });
  };

  // Handle create watchlist
  const handleCreateWatchlist = async () => {
    if (!newName.trim()) return;

    try {
      await createWatchlist.mutateAsync(newName.trim());
      toast.success(t("checklist.template-created"));
      setIsCreateOpen(false);
      setNewName("");
    } catch (error) {
      // Extract actual error message
      let errorMessage = t("checklist.create-failed");
      if (error instanceof Error) {
        // Parse error message for user-friendly details
        const message = error.message;
        if (message.includes("already exists")) {
          errorMessage = `Watchlist "${newName.trim()}" already exists`;
        } else if (message.includes("duplicate key")) {
          errorMessage = `Watchlist "${newName.trim()}" already exists`;
        } else {
          // Show detailed error for debugging
          console.error("Create watchlist error:", error);
          errorMessage = `Failed to create watchlist: ${message}`;
        }
      }
      toast.error(errorMessage);
    }
  };

  // Handle rename watchlist
  const handleRenameWatchlist = async () => {
    if (!selectedWatchlist || !newName.trim()) return;

    try {
      await updateWatchlist.mutateAsync({
        id: selectedWatchlist.id,
        name: newName.trim(),
      });
      toast.success(t("checklist.template-updated"));
      setIsRenameOpen(false);
      setNewName("");
      setSelectedWatchlist(null);
    } catch (error) {
      // Extract actual error message
      let errorMessage = t("checklist.update-failed");
      if (error instanceof Error) {
        const message = error.message;
        if (message.includes("already exists")) {
          errorMessage = `Watchlist "${newName.trim()}" already exists`;
        } else if (message.includes("duplicate key")) {
          errorMessage = `Watchlist "${newName.trim()}" already exists`;
        } else {
          console.error("Rename watchlist error:", error);
          errorMessage = `Failed to rename watchlist: ${message}`;
        }
      }
      toast.error(errorMessage);
    }
  };

  // Handle delete watchlist
  const handleDeleteWatchlist = async () => {
    if (!selectedWatchlist) return;

    try {
      await deleteWatchlist.mutateAsync(selectedWatchlist.id);
      toast.success(t("checklist.template-deleted"));
      setIsDeleteOpen(false);
      setSelectedWatchlist(null);
      // Navigate to "all" if we deleted the current watchlist
      if (selectedWatchlistId === selectedWatchlist.id) {
        setSearchParams({ watchlist: "all" });
      }
    } catch (error) {
      toast.error(t("checklist.delete-failed"));
      console.error(error);
    }
  };

  // Filter watchlists by search query
  const filteredWatchlists = watchlists.filter((w) => w.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <aside
      className={cn(
        "flex flex-col justify-start items-start bg-background text-sidebar-foreground transition-all duration-300 h-full w-full",
        // Desktop: right border (unless collapsed)
        !inDrawer && !isCollapsed && "border-r border-border",
        // Mobile drawer: no border, transparent background
        inDrawer && "border-none bg-transparent",
        className,
      )}
    >
      {/* Header */}
      {!inDrawer && !isCollapsed && (
        <div className="w-full flex-shrink-0 px-3 pt-6 pb-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <List className="h-5 w-5 text-sidebar-foreground" strokeWidth={2} />
              <h2 className="text-base font-semibold text-sidebar-foreground">{t("watchlist.title")}</h2>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="收起">
                <ChevronLeft className="h-4 w-4 text-sidebar-foreground" strokeWidth={2} />
              </button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={2.5} />
              </Button>
            </div>
          </div>

          {/* Search */}
          {!inDrawer && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2} />
              <Input
                placeholder={t("watchlist.search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-9 pr-8 rounded-lg text-sm bg-muted/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-accent transition-colors"
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2.5} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Collapsed Header - Icon only */}
      {!inDrawer && isCollapsed && (
        <div className="w-full flex-shrink-0 px-2 pt-4 pb-2 flex flex-col items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setIsCreateOpen(true)}>
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t("watchlist.new_watchlist")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <button onClick={onToggle} className="p-2 rounded-lg hover:bg-muted transition-colors">
                    <ChevronRight className="h-4 w-4 text-sidebar-foreground" strokeWidth={2} />
                  </button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>展开</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* In-Drawer Header (Compact) */}
      {inDrawer && (
        <div className="w-full px-4 pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              <span className="text-sm">{t("watchlist.new_watchlist")}</span>
            </Button>
          </div>
        </div>
      )}

      {/* Watchlist List */}
      <div className="flex-1 w-full overflow-y-auto overflow-x-hidden px-2 py-2 space-y-1 custom-scrollbar min-h-0">
        {/* "All Tickers" - Always visible */}
        {isCollapsed ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleSelectWatchlist("all")}
                  className={cn(
                    "w-full flex items-center justify-center gap-3 px-3 py-2.5 rounded-lg border transition-all duration-200 group",
                    selectedWatchlistId === "all"
                      ? "bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-accent-border"
                      : "border-transparent hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:border-sidebar-accent-border opacity-80",
                  )}
                >
                  <Sparkles className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t("watchlist.all_tickers")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <button
            onClick={() => handleSelectWatchlist("all")}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all duration-200 group",
              selectedWatchlistId === "all"
                ? "bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-accent-border"
                : "border-transparent hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:border-sidebar-accent-border opacity-80",
            )}
            title={t("watchlist.all_tickers")}
          >
            <Sparkles className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-sm font-medium truncate">{t("watchlist.all_tickers")}</span>
              {(allTickersData?.totalSize ?? 0) > 0 && <span className="text-xs text-muted-foreground">({allTickersData?.totalSize})</span>}
            </div>
          </button>
        )}

        {/* No watchlists empty state */}
        {watchlists.length === 0 && (
          <div className="px-3 py-8 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
              <FolderOpen className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h4 className="text-sm font-medium text-foreground mb-1">{t("watchlist.empty_state.title")}</h4>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed max-w-[200px]">{t("watchlist.empty_state.description")}</p>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
              <span className="text-xs">{t("watchlist.empty_state.create_first")}</span>
            </Button>
          </div>
        )}

        {/* User watchlists */}
        {watchlists.length > 0 &&
          filteredWatchlists.map((watchlist) => (
            <div
              key={watchlist.id}
              className={cn(
                "group flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all duration-200",
                selectedWatchlistId === watchlist.id
                  ? "bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-accent-border"
                  : "border-transparent hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:border-sidebar-accent-border opacity-80",
              )}
            >
              {isCollapsed ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-full">
                        <button
                          onClick={() => handleSelectWatchlist(watchlist.id)}
                          className="flex items-center justify-center"
                          title={watchlist.name}
                        >
                          <div className="h-5 w-5 rounded bg-muted flex-shrink-0 flex items-center justify-center text-[9px] font-semibold">
                            {watchlist.name.charAt(0).toUpperCase()}
                          </div>
                        </button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{watchlist.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <>
                  <button
                    onClick={() => handleSelectWatchlist(watchlist.id)}
                    className="flex items-center gap-3 flex-1 min-w-0"
                    title={watchlist.name}
                  >
                    <div className="h-5 w-5 rounded bg-muted flex-shrink-0 flex items-center justify-center text-[9px] font-semibold">
                      {watchlist.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{watchlist.name}</span>
                        {(watchlist.tickerCount ?? 0) > 0 && (
                          <span className="text-xs text-muted-foreground">({watchlist.tickerCount})</span>
                        )}
                      </div>
                    </div>
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedWatchlist(watchlist);
                          setNewName(watchlist.name);
                          setIsRenameOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-2" strokeWidth={2} />
                        <span className="text-xs">{t("watchlist.rename")}</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setSelectedWatchlist(watchlist);
                          setIsDeleteOpen(true);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" strokeWidth={2} />
                        <span className="text-xs">{t("watchlist.delete")}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          ))}

        {/* Search no results */}
        {watchlists.length > 0 && filteredWatchlists.length === 0 && searchQuery && (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">No watchlists found</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      {isCreateOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-md border border-border">
            <h3 className="text-lg font-semibold mb-4">{t("watchlist.create_watchlist")}</h3>
            <Input
              placeholder={t("watchlist.untitled")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !createWatchlist.isPending) {
                  e.preventDefault();
                  handleCreateWatchlist();
                }
              }}
              disabled={createWatchlist.isPending}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateOpen(false);
                  setNewName("");
                }}
                disabled={createWatchlist.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={handleCreateWatchlist} disabled={createWatchlist.isPending || !newName.trim()}>
                {createWatchlist.isPending ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Dialog */}
      {isRenameOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-md border border-border">
            <h3 className="text-lg font-semibold mb-4">{t("watchlist.rename_watchlist")}</h3>
            <Input
              placeholder={t("watchlist.untitled")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !updateWatchlist.isPending) {
                  e.preventDefault();
                  handleRenameWatchlist();
                }
              }}
              disabled={updateWatchlist.isPending}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsRenameOpen(false);
                  setNewName("");
                  setSelectedWatchlist(null);
                }}
                disabled={updateWatchlist.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={handleRenameWatchlist} disabled={updateWatchlist.isPending || !newName.trim()}>
                {updateWatchlist.isPending ? t("common.save") : t("watchlist.rename")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteOpen && selectedWatchlist && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-md border border-border">
            <h3 className="text-lg font-semibold mb-2">{t("watchlist.confirm_delete")}</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {t("watchlist.confirm_delete_note")}
              <br />
              <br />
              <strong>{selectedWatchlist.name}</strong>
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteOpen(false);
                  setSelectedWatchlist(null);
                }}
                disabled={deleteWatchlist.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleDeleteWatchlist} disabled={deleteWatchlist.isPending}>
                {deleteWatchlist.isPending ? t("common.save") : t("watchlist.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
